import { Stack, Typography } from '@mui/material';
import { Trans } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import TVAccordion from './TVAccordion/TVAccordion';
import Outdoor from './Outdoor/Outdoor';
import { Touchpoint } from '../../../../../../../api/services/mediaPlanning/touchpoints';
import { Touchpoints } from '../../../enums';
import {
  Buttons,
  CalculateButton,
} from '../../../../../../../shared/components/ParametersForm/styles';
import CaptionedLinearLoader from '../../../../components/Touchpoints/CaptionedLinearLoader/CaptionedLinearLoader';
import EstimatedTVBudgetTable from './TVAccordion/EstimatedTVBudgetTable/OfflineBudgetTable/EstimatedTVBudgetTable';
import EstimatedOOHBudgetTable from './TVAccordion/EstimatedOOHBudgetTable/EstimatedOOHBudgetTable';
import AddedToPlanSnackbar from '../Digital/AddedToPlanSnackbar/AddedToPlanSnackbar';
import ResetToDefaultsDialog from '../../CountryPanel/ResetToDefaultsDialog/ResetToDefaultsDialog';
import getGrpTableValidation from './TVAccordion/utils/getGrpTableValidation';
import useEstimatedBudgetColumns from './TVAccordion/EstimatedOOHBudgetTable/hooks/useEstimatedBudgetColumns';
import queryClient from '../../../../../../../config/queryClient';
import { OFFLINE } from '../../../../../../../api/services/mediaPlanning/offline/setup';
import useEstimatedTVBudget from './hooks/useEstimatedTVBudget';
import useEstimatedOOHBudget from './hooks/useEstimatedOOHBudget';
import usePlanStore, { TVConfig } from '../../../../../../../store/planStore';
import useCalculateOneReach from '../../hooks/useCalculateOneReach';
import { Countries } from '../../../../../../../shared/enums';

export interface OfflineProps {
  availableTouchpoints: Touchpoint[];
}

export default function Offline({ availableTouchpoints }: OfflineProps) {
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showResetToDefaultsDialog, setShowResetToDefaultsDialog] =
    useState(false);

  const tvTouchpoint = availableTouchpoints.find(
    (touchpoint) => touchpoint.TouchpointName === Touchpoints.tv
  );

  const oohTouchpoint = availableTouchpoints.find(
    (touchpoint) => touchpoint.TouchpointName === Touchpoints.ooh
  );

  const {
    countryForms,
    selectedCountry,
    selectedCountryISOCode,
    savedCountryForms,
    updateOfflineBudget,
    resetOfflineBudget,
    savedGeneralInfoState,
    resetOfflineTables,
    saveCountryFormState,
    updateOOHAvailable,
    updateTVAvailable,
    resetTVConfig,
    resetOOHConfig,
    saveOfflineCountryFormState,
    storeOfflineCountryFormState,
  } = usePlanStore();

  const { asyncCalculateOneReach } = useCalculateOneReach();

  const hasMetaAwareness = availableTouchpoints.some(
    (touchpoint) =>
      touchpoint.TouchpointName === 'META' &&
      touchpoint.touchpointFunnel.toLowerCase().includes('awareness')
  );

  useEffect(() => {
    if (tvTouchpoint?.isMustHave) {
      updateTVAvailable(selectedCountry!, !!tvTouchpoint.isMustHave);
    }

    if (oohTouchpoint?.isMustHave) {
      updateOOHAvailable(selectedCountry!, !!oohTouchpoint.isMustHave);
    }
  }, [tvTouchpoint, oohTouchpoint]);

  const formData = countryForms[selectedCountry!]?.offline;

  const { calculateBudget: calculateTVBudget, isLoading: isTVBudgetLoading } =
    useEstimatedTVBudget();
  const { calculateBudget: calculateOOHBudget, isLoading: isOOHBudgetLoading } =
    useEstimatedOOHBudget(formData?.ooh?.isAvailable);

  const handleResetToDefaults = () => {
    resetOfflineBudget(selectedCountry!);
    resetOfflineTables(selectedCountry!);
    resetTVConfig(selectedCountry!);
    resetOOHConfig(selectedCountry!);
    updateTVAvailable(selectedCountry!, !!tvTouchpoint?.isMustHave);
    updateOOHAvailable(selectedCountry!, !!oohTouchpoint?.isMustHave);
    queryClient.invalidateQueries({
      queryKey: [
        OFFLINE,
        'tv-setup',
        selectedCountry,
        selectedCountryISOCode,
        savedGeneralInfoState,
        countryForms[selectedCountry!].offline?.tv?.targetGroup,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        OFFLINE,
        'campaign-types',
        selectedCountry,
        selectedCountryISOCode,
        countryForms[selectedCountry!]?.targetGroup,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [
        OFFLINE,
        'tv-target-groups',
        selectedCountry,
        selectedCountryISOCode,
        savedGeneralInfoState,
      ],
    });
    queryClient.invalidateQueries({
      queryKey: [OFFLINE, 'copy-lengths'],
    });
    queryClient.invalidateQueries({
      queryKey: [
        OFFLINE,
        'default-target-group',
        selectedCountry,
        selectedCountryISOCode,
        savedCountryForms[selectedCountry!]?.targetGroup,
        countryForms[selectedCountry!].offline?.tv?.isAvailable,
      ],
    });
    setShowResetToDefaultsDialog(false);
  };

  const handleCalculateBudget = () => {
    calculateTVBudget();
    calculateOOHBudget();
  };

  const estimatedOOHBudgetTableColumns = useEstimatedBudgetColumns(
    countryForms[selectedCountry!].offline?.ooh?.calculatedBudget?.data[0]
      ?.budget.currency ?? 'PLN'
  );

  const handleAddToPlan = async () => {
    if (formData?.tv?.calculatedBudget || formData?.ooh?.calculatedBudget) {
      updateOfflineBudget(selectedCountry!);

      saveOfflineCountryFormState(selectedCountry!);
      storeOfflineCountryFormState(selectedCountry!);
      if (selectedCountryISOCode === Countries.POLAND && hasMetaAwareness) {
        await asyncCalculateOneReach();
      }
      saveOfflineCountryFormState(selectedCountry!);
      storeOfflineCountryFormState(selectedCountry!);
      setShowSnackbar(true);
    }
  };

  const isGRPTableInvalid = getGrpTableValidation(formData?.tv);

  const getDisabledCalculateButton = () => {
    const countryOffline = countryForms[selectedCountry!].offline;
    const settedFormIsEqualToCurrentForm =
      formData?.tv?.calculatedBudget &&
      countryForms[selectedCountry!].offline ===
        savedCountryForms[selectedCountry!].offline;

    if (!countryOffline?.tv?.isAvailable && !countryOffline?.ooh?.isAvailable) {
      return true;
    }
    if (!tvTouchpoint && oohTouchpoint) {
      return !countryOffline?.ooh?.isAvailable;
    }
    if (settedFormIsEqualToCurrentForm) {
      return true;
    }

    return (
      countryOffline?.tv?.isAvailable &&
      (!countryOffline?.tv?.grpTable || isGRPTableInvalid)
    );
  };

  return (
    <Stack sx={{ background: 'white', mt: 3 }}>
      <Outdoor
        disabledSwitch={!oohTouchpoint ?? !!oohTouchpoint?.isMustHave}
        isMustHave={!!oohTouchpoint?.isMustHave}
      />
      <TVAccordion
        disabledSwitch={!tvTouchpoint || !!tvTouchpoint?.isMustHave}
        isMustHave={!!tvTouchpoint?.isMustHave}
      />
      <Buttons
        item
        xs
        sx={{ fontWeight: 700, mt: '40px', pb: '40px', alignSelf: 'flex-end' }}
      >
        <CalculateButton
          sx={{
            outline: 1,
            minWidth: '9rem',
            borderRadius: '30px',
            marginRight: '33px',
          }}
          variant="outlined"
          onClick={() => {
            setShowResetToDefaultsDialog(true);
          }}
        >
          Reset to defaults
        </CalculateButton>
        <CalculateButton
          sx={{
            borderRadius: '30px',
            marginRight: '24px',
          }}
          onClick={handleCalculateBudget}
          variant="contained"
          disabled={getDisabledCalculateButton()}
        >
          {formData?.tv?.calculatedBudget ? 'Recalculate' : 'Calculate'}
        </CalculateButton>
      </Buttons>
      {isTVBudgetLoading && (
        <CaptionedLinearLoader
          isScrollable
          caption={
            <Typography textAlign="center">
              <Trans i18nKey="loaders.holdOn" />
            </Typography>
          }
        />
      )}
      {formData?.tv?.calculatedBudget &&
        !isTVBudgetLoading &&
        !isOOHBudgetLoading && (
          <EstimatedTVBudgetTable
            data={formData?.tv.calculatedBudget}
            formData={formData}
          />
        )}
      {isOOHBudgetLoading && (
        <CaptionedLinearLoader
          isScrollable
          caption={
            <Typography textAlign="center">
              <Trans i18nKey="loaders.holdOn" />
            </Typography>
          }
        />
      )}
      {formData?.ooh?.calculatedBudget?.data.length &&
      !isOOHBudgetLoading &&
      !isTVBudgetLoading ? (
        <EstimatedOOHBudgetTable
          columns={estimatedOOHBudgetTableColumns}
          rows={formData?.ooh?.calculatedBudget.data}
        />
      ) : null}
      <Buttons
        item
        xs
        sx={{ fontWeight: 700, mt: '40px', pb: '40px', alignSelf: 'flex-end' }}
      >
        {(formData?.tv?.calculatedBudget ||
          formData?.ooh?.calculatedBudget?.data.length) &&
        !isTVBudgetLoading &&
        !isOOHBudgetLoading ? (
          <CalculateButton
            sx={{
              borderRadius: '30px',
              marginRight: '24px',
            }}
            onClick={handleAddToPlan}
            variant="contained"
          >
            Add to plan
          </CalculateButton>
        ) : null}
      </Buttons>
      <AddedToPlanSnackbar
        showSnackbar={showSnackbar}
        setShowSnackbar={setShowSnackbar}
      />
      <ResetToDefaultsDialog
        onClose={() => setShowResetToDefaultsDialog(false)}
        onSuccess={handleResetToDefaults}
        open={showResetToDefaultsDialog}
        text="Are you sure you want to clear this form?"
      />
    </Stack>
  );
}
